import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Bike, Bus, Car, CheckCircle, Truck, type LucideIcon } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '../ui'

const ALL_CATEGORY_CODES = ['A', 'B', 'C', 'D'] as const

/** Lucide icons bundle with the app — avoids `/src/assets/*.png` URLs that break after Vite build/deploy. */
const CATEGORY_ICON_COMPONENTS: Record<(typeof ALL_CATEGORY_CODES)[number], LucideIcon> = {
  A: Bike,
  B: Car,
  C: Truck,
  D: Bus,
}

interface VehicleCategorySelectorProps {
  selectedCategory: string
  onCategoryChange: (category: string) => void
  /** When set, only these category codes (e.g. A–D) are shown. */
  allowedCategoryCodes?: string[]
  disabled?: boolean
}

export const VehicleCategorySelector: React.FC<VehicleCategorySelectorProps> = ({
  selectedCategory,
  onCategoryChange,
  allowedCategoryCodes,
  disabled = false,
}) => {
  const { t } = useTranslation()

  const categories = useMemo(() => {
    const allowed =
      allowedCategoryCodes && allowedCategoryCodes.length > 0
        ? new Set(allowedCategoryCodes)
        : new Set<string>(ALL_CATEGORY_CODES)
    return ALL_CATEGORY_CODES.filter(code => allowed.has(code)).map(value => ({
      value,
      label: t(`vehicles.registration.categoryLabels.${value}` as const),
      Icon: CATEGORY_ICON_COMPONENTS[value],
    }))
  }, [allowedCategoryCodes, t])

  return (
    <Card className={disabled ? 'opacity-60 pointer-events-none' : ''}>
      <CardHeader>
        <CardTitle>{t('vehicles.registration.selectVehicleTypeTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <label className="block text-sm font-medium text-foreground">
            {t('vehicles.registration.vehicleCategoryLabel')}
          </label>
          {categories.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('vehicles.registration.noCategoriesForBranch')}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.map(category => {
                const Icon = category.Icon
                return (
                <label
                  key={category.value}
                  className={`relative flex flex-col items-center p-4 border-2 rounded-xl transition-all group ${
                    selectedCategory === category.value
                      ? 'border-accent bg-accent/5 dark:bg-primary-900/20 shadow-md cursor-pointer'
                      : disabled
                        ? 'border-border cursor-not-allowed'
                        : 'border-border hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm cursor-pointer'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={category.value}
                    checked={selectedCategory === category.value}
                    onChange={e => onCategoryChange(e.target.value)}
                    disabled={disabled}
                    className="sr-only"
                  />
                  <div
                    className={`w-16 h-16 mb-3 flex items-center justify-center rounded-lg transition-colors ${
                      selectedCategory === category.value
                        ? 'bg-accent/10 dark:bg-primary-900/30'
                        : 'bg-surface group-hover:bg-accent/5 dark:group-hover:bg-primary-900/10'
                    }`}
                  >
                    <Icon
                      className="h-12 w-12 object-contain text-foreground/80"
                      aria-hidden
                      strokeWidth={1.5}
                    />
                  </div>
                  <span
                    className={`text-sm font-medium text-center ${
                      selectedCategory === category.value
                        ? 'text-primary-700 dark:text-primary-300'
                        : 'text-foreground'
                    }`}
                  >
                    {category.label}
                  </span>
                  {selectedCategory === category.value && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className="h-5 w-5 text-accent" />
                    </div>
                  )}
                </label>
                )
              })}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
